import { fetchAuthSession } from 'aws-amplify/auth';
import { UserRole } from '../types/user';

// API Gateway endpoint for Cognito admin operations
// This should be set in your environment variables
// Using the correct endpoint from Terraform: https://sjj37gb77b.execute-api.us-east-1.amazonaws.com/prod
const ADMIN_API_ENDPOINT = process.env.REACT_APP_ADMIN_API_ENDPOINT || '';

interface CreateCognitoUserInput {
  email: string;
  name: string;
  employeeId: string;
  department: string;
  managerEmail?: string;
  role: UserRole;
  shift?: string;
  dateOfJoining?: string;
  dateOfExit?: string;
  employmentType?: string;
  /** Team / product line — persisted in UserProfiles by the Lambda, not Cognito. */
  team?: string;
  /** Date of birth — same store, same reason. */
  dateOfBirth?: string;
  /** The employee's code on the biometric device, when it differs from employeeId. */
  deviceCode?: string;
  /** Mobile number in E.164 for the attendance shortfall SMS — UserProfiles, never Cognito. */
  phone?: string;
  /** Aadhaar, 12 digits — UserProfiles-backed, never Cognito. Optional. */
  aadhaarNumber?: string;
  /** PAN in ABCDE1234F form — UserProfiles-backed, never Cognito. Optional. */
  panNumber?: string;
  temporaryPassword?: string; // Optional - if not provided, user will receive invitation email
}

interface UpdateCognitoUserInput {
  email: string;
  name?: string;
  employeeId?: string;
  department?: string;
  managerEmail?: string;
  role?: UserRole;
  shift?: string;
  dateOfJoining?: string;
  dateOfExit?: string;
  employmentType?: string;
  /** Team / product line — persisted in UserProfiles by the Lambda, not Cognito. */
  team?: string;
  /** Date of birth — same store, same reason. */
  dateOfBirth?: string;
  /** The employee's code on the biometric device, when it differs from employeeId. */
  deviceCode?: string;
  /** Mobile number in E.164 for the attendance shortfall SMS — UserProfiles, never Cognito. */
  phone?: string;
  /** Aadhaar, 12 digits — UserProfiles-backed, never Cognito. Optional. */
  aadhaarNumber?: string;
  /** PAN in ABCDE1234F form — UserProfiles-backed, never Cognito. Optional. */
  panNumber?: string;
}

interface CognitoAdminResponse {
  success: boolean;
  message: string;
  total?: number;
  /** True when some fields were saved but others were rejected by the user pool schema. */
  partial?: boolean;
  /** Cognito attribute names (e.g. 'custom:shift') the pool could not store. */
  skippedAttributes?: string[];
  user?: {
    username: string;
    email: string;
    status: string;
  };
}

/**
 * Service for Cognito Admin operations via API Gateway + Lambda
 * Requires Super Admin role to use these functions
 */
export class CognitoAdminService {
  
  /**
   * Map raw role string from Cognito to UserRole enum
   */
  static mapCognitoRole(rawRole: string | undefined): UserRole {
    console.log(`[CognitoAdminService] mapCognitoRole called with:`, rawRole);
    
    if (!rawRole) {
      console.log(`[CognitoAdminService] No role provided, returning Employee default`);
      return UserRole.EMPLOYEE; // Default fallback
    }
    
    const roleStr = rawRole.toString().trim();
    console.log(`[CognitoAdminService] Processing role string:`, roleStr);
    
    // Direct mapping - check if it's already a valid UserRole
    if (Object.values(UserRole).includes(roleStr as UserRole)) {
      console.log(`[CognitoAdminService] Direct match found:`, roleStr);
      return roleStr as UserRole;
    }
    
    // Handle the specific role formats from your Cognito response
    const roleMapping: { [key: string]: UserRole } = {
      // Exact matches from your Cognito response
      'EMPLOYEE': UserRole.EMPLOYEE,
      'MANAGER': UserRole.MANAGER,
      'FINANCE_ADMIN': UserRole.FINANCE_ADMIN,
      'Super_Admin': UserRole.SUPER_ADMIN,
      'HR_ADMIN': UserRole.HR_ADMIN,
      'HR_Admin': UserRole.HR_ADMIN,
      
      // Case variations
      'employee': UserRole.EMPLOYEE,
      'manager': UserRole.MANAGER,
      'Manager': UserRole.MANAGER,
      'finance_admin': UserRole.FINANCE_ADMIN,
      'financeadmin': UserRole.FINANCE_ADMIN,
      'finance admin': UserRole.FINANCE_ADMIN,
      'super_admin': UserRole.SUPER_ADMIN,
      'superadmin': UserRole.SUPER_ADMIN,
      'super admin': UserRole.SUPER_ADMIN,
      'hr_admin': UserRole.HR_ADMIN,
      'hradmin': UserRole.HR_ADMIN,
      'hr admin': UserRole.HR_ADMIN,
      'admin': UserRole.FINANCE_ADMIN // Common alias
    };
    
    // Try exact match first
    const exactMatch = roleMapping[roleStr];
    if (exactMatch) {
      console.log(`[CognitoAdminService] Exact match found:`, roleStr, '→', exactMatch);
      return exactMatch;
    }
    
    // Try case-insensitive match
    const lowerMatch = roleMapping[roleStr.toLowerCase()];
    if (lowerMatch) {
      console.log(`[CognitoAdminService] Case-insensitive match found:`, roleStr.toLowerCase(), '→', lowerMatch);
      return lowerMatch;
    }
    
    // Try normalized match (remove spaces, underscores, hyphens)
    const normalizedRole = roleStr.toLowerCase().replace(/[_\s-]/g, '');
    const normalizedMatch = roleMapping[normalizedRole];
    if (normalizedMatch) {
      console.log(`[CognitoAdminService] Normalized match found:`, normalizedRole, '→', normalizedMatch);
      return normalizedMatch;
    }
    
    console.warn(`[CognitoAdminService] Unknown role format: "${roleStr}", using default Employee`);
    return UserRole.EMPLOYEE;
  }
  
  /**
   * Get authorization headers with current user's token
   */
  private static async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      return {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'Access-Control-Request-Headers': 'Content-Type,Authorization',
        'Access-Control-Request-Method': 'GET,POST,PUT,DELETE'
      };
    } catch (error) {
      console.error('Failed to get auth session:', error);
      return {
        'Content-Type': 'application/json',
        'Access-Control-Request-Headers': 'Content-Type,Authorization',
        'Access-Control-Request-Method': 'GET,POST,PUT,DELETE'
      };
    }
  }

  /**
   * Check if Cognito Admin API is configured
   */
  static isConfigured(): boolean {
    console.log(`[CognitoAdminService] Environment variable check:`, {
      'process.env.REACT_APP_ADMIN_API_ENDPOINT': process.env.REACT_APP_ADMIN_API_ENDPOINT,
      'ADMIN_API_ENDPOINT constant': ADMIN_API_ENDPOINT,
      'typeof ADMIN_API_ENDPOINT': typeof ADMIN_API_ENDPOINT,
      'ADMIN_API_ENDPOINT.length': ADMIN_API_ENDPOINT?.length,
      'All REACT_APP env vars': Object.keys(process.env).filter(key => key.startsWith('REACT_APP'))
    });
    
    const isConfigured = !!ADMIN_API_ENDPOINT && ADMIN_API_ENDPOINT.length > 0;
    console.log(`[CognitoAdminService] Configuration result:`, isConfigured);
    return isConfigured;
  }

  /**
   * Create a new user in Cognito
   * This will create the user with a temporary password and send an email invitation
   */
  static async createUser(input: CreateCognitoUserInput): Promise<CognitoAdminResponse> {
    if (!this.isConfigured()) {
      throw new Error('Cognito Admin API is not configured. Please set REACT_APP_ADMIN_API_ENDPOINT.');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${ADMIN_API_ENDPOINT}/users`, {
        method: 'POST',
        headers,
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          email: input.email,
          name: input.name,
          employeeId: input.employeeId,
          department: input.department,
          managerEmail: input.managerEmail || '',
          role: input.role,
          shift: input.shift,
          dateOfJoining: input.dateOfJoining,
          dateOfExit: input.dateOfExit,
          employmentType: input.employmentType,
          team: input.team,
          dateOfBirth: input.dateOfBirth,
          deviceCode: input.deviceCode,
          phone: input.phone,
          aadhaarNumber: input.aadhaarNumber,
          panNumber: input.panNumber,
          temporaryPassword: input.temporaryPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to create user: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to create Cognito user:', error);
      throw error;
    }
  }

  /**
   * Update user attributes in Cognito
   * Updates both the email-based user and Azure SSO user if they exist
   */
  static async updateUser(input: UpdateCognitoUserInput): Promise<CognitoAdminResponse> {
    if (!this.isConfigured()) {
      throw new Error('Cognito Admin API is not configured. Please set REACT_APP_ADMIN_API_ENDPOINT.');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      console.log(`[CognitoAdminService] Updating user: ${input.email}`);
      console.log(`[CognitoAdminService] Update data:`, input);
      console.log(`[CognitoAdminService] API endpoint:`, `${ADMIN_API_ENDPOINT}/users/${encodeURIComponent(input.email)}`);
      
      const response = await fetch(`${ADMIN_API_ENDPOINT}/users/${encodeURIComponent(input.email)}`, {
        method: 'PUT',
        headers,
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          name: input.name,
          employeeId: input.employeeId,
          department: input.department,
          managerEmail: input.managerEmail,
          role: input.role,
          shift: input.shift,
          dateOfJoining: input.dateOfJoining,
          dateOfExit: input.dateOfExit,
          employmentType: input.employmentType,
          team: input.team,
          dateOfBirth: input.dateOfBirth,
          deviceCode: input.deviceCode,
          phone: input.phone,
          aadhaarNumber: input.aadhaarNumber,
          panNumber: input.panNumber
        })
      });

      console.log(`[CognitoAdminService] Response status:`, response.status);

      const data = await response.json();
      console.log(`[CognitoAdminService] Response data:`, data);

      if (!response.ok) {
        throw new Error(data.message || `Failed to update user: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to update Cognito user:', error);
      
      // Provide specific error messages for common issues
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('CORS Error: Unable to connect to the API. Please check that CORS is properly configured on your API Gateway for localhost:3000');
      }
      
      throw error;
    }
  }

  /**
   * Delete/disable a user in Cognito
   */
  static async deleteUser(email: string): Promise<CognitoAdminResponse> {
    if (!this.isConfigured()) {
      throw new Error('Cognito Admin API is not configured. Please set REACT_APP_ADMIN_API_ENDPOINT.');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${ADMIN_API_ENDPOINT}/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers,
        mode: 'cors',
        credentials: 'omit'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to delete user: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to delete Cognito user:', error);
      throw error;
    }
  }

  /**
   * List all users from Cognito
   */
  static async listUsers(): Promise<CognitoAdminResponse & { users?: any[] }> {
    if (!this.isConfigured()) {
      throw new Error('Cognito Admin API is not configured. Please set REACT_APP_ADMIN_API_ENDPOINT.');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      console.log(`[CognitoAdminService] Listing users from:`, `${ADMIN_API_ENDPOINT}/users`);
      console.log(`[CognitoAdminService] Headers:`, headers);
      
      const response = await fetch(`${ADMIN_API_ENDPOINT}/users`, {
        method: 'GET',
        headers,
        mode: 'cors',
        credentials: 'omit'
      });

      console.log(`[CognitoAdminService] Response status:`, response.status);
      console.log(`[CognitoAdminService] Response headers:`, Object.fromEntries(response.headers.entries()));
      
      // Check if response is HTML (error page)
      const contentType = response.headers.get('content-type');
      console.log(`[CognitoAdminService] Content-Type:`, contentType);
      
      if (contentType && contentType.includes('text/html')) {
        const htmlText = await response.text();
        console.error(`[CognitoAdminService] Received HTML instead of JSON:`, htmlText.substring(0, 500));
        throw new Error(`API returned HTML instead of JSON. Status: ${response.status}. This usually indicates a server error or incorrect endpoint.`);
      }

      const responseText = await response.text();
      console.log(`[CognitoAdminService] Raw response:`, responseText.substring(0, 1000));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error(`[CognitoAdminService] Failed to parse JSON:`, parseError);
        console.error(`[CognitoAdminService] Response text:`, responseText);
        throw new Error(`Invalid JSON response from API. Response: ${responseText.substring(0, 200)}`);
      }

      console.log(`[CognitoAdminService] total Cognito users returned by API:`, {
        total: data.total,
        usersLength: Array.isArray(data.users) ? data.users.length : 0,
      });

      if (!response.ok) {
        throw new Error(data.message || `Failed to list users: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to list Cognito users:', error);
      throw error;
    }
  }

  /**
   * Reset user password (sends new temporary password)
   */
  static async resetUserPassword(email: string): Promise<CognitoAdminResponse> {
    if (!this.isConfigured()) {
      throw new Error('Cognito Admin API is not configured. Please set REACT_APP_ADMIN_API_ENDPOINT.');
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${ADMIN_API_ENDPOINT}/users/${encodeURIComponent(email)}/reset-password`, {
        method: 'POST',
        headers,
        mode: 'cors',
        credentials: 'omit'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to reset password: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to reset user password:', error);
      throw error;
    }
  }
}
